import { useAppState } from '@/hooks/useAppState';
import { http } from '@/utils/http';
import {
  AcceptFrisbiiSessionSchema,
  CancelFrisbiiSessionSchema,
  CreateFrisbiiSessionSchemaFrontend,
} from '@/utils/validations/frisbii';
import { AxiosError } from 'axios';
import { FrisbiiError } from 'lib/errors/FrisbiiError';
import { INVALID_ACCESS_TOKEN } from 'lib/paymentsV2/redirectToRefreshIfInvalidToken';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import useWindowDataLayer, { type EcommerceItem } from './useWindowDataLayer';

export type SessionPayload = z.infer<typeof CreateFrisbiiSessionSchemaFrontend>;
export type AcceptSessionPayload = z.infer<typeof AcceptFrisbiiSessionSchema> & Record<string, any>;
export type CancelSessionPayload = z.infer<typeof CancelFrisbiiSessionSchema> & Record<string, any>;
export type FrisbiiPurchaseTracking = { currency?: string; value?: number; items?: EcommerceItem[] };

export const useFrisbiiPayment = () => {
  const [{ frisbiiSession }, appStateDispatcher] = useAppState();
  const { push: dataLayerPush } = useWindowDataLayer();

  const dispatchFrisbiiSession = useCallback(
    (newSession: Partial<typeof frisbiiSession> | undefined) => {
      appStateDispatcher({
        frisbiiSession: !newSession
          ? newSession
          : {
              ...frisbiiSession,
              ...newSession,
            },
      });
    },
    [appStateDispatcher, frisbiiSession],
  );

  const createFrisbiiSession = useCallback(
    async (payload: Partial<SessionPayload> = {}) => {
      const response = await http
        .post<{ session_id: string; signature: string; customer: string; subscription: string }>(
          '/api/payments/v2/create-frisbii-session',
          { ...payload, referrer: payload.referrer || window.location.href },
          {
            skipInterceptors: { response: { rejected: true } },
          },
        )
        .catch((error: AxiosError<{ error?: string }>) => {
          if (error.response?.status === 409) {
            throw new FrisbiiError('account_exists', error);
          }

          if (error.response?.status === 400 && error.response?.data?.error === 'entitlements_exists') {
            throw new FrisbiiError('already_subscribed', error);
          }

          if (error.response?.status === 401 && error.response?.data?.error === INVALID_ACCESS_TOKEN) {
            throw new FrisbiiError('session_expired', error);
          }

          throw new FrisbiiError('unknown', error);
        });

      if (!response?.data) {
        throw new FrisbiiError('unknown');
      }

      const session = {
        sessionId: response.data.session_id,
        signature: response.data.signature,
        customer: response.data.customer,
        subscription: response.data.subscription,
        purchaseCompleted: false,
      };
      dispatchFrisbiiSession(session);

      return session;
    },
    [dispatchFrisbiiSession],
  );

  const onAcceptEvent = useCallback(async (payload: AcceptSessionPayload, tracking?: FrisbiiPurchaseTracking) => {
    const response = await http
      .post<{ redirect_to: string }>('/api/payments/v2/accept-frisbii-session', payload, {
        skipInterceptors: { response: { rejected: true } },
      })
      .catch((error: AxiosError<{ error?: string }>) => {
        if (error.response?.status === 401 && error.response?.data?.error === INVALID_ACCESS_TOKEN) {
          throw new FrisbiiError('session_expired', error);
        }
        throw new FrisbiiError('unknown', error);
      });
    if (!response?.data) {
      throw new FrisbiiError('unknown');
    }

    dataLayerPush({
      event: 'purchase',
      ecommerce: {
        transaction_id: payload.subscription || '',
        subscription_id: payload.subscription || '',
        currency: tracking?.currency ?? 'sek',
        value: tracking?.value ?? 29,
        items: tracking?.items ?? [{ price: 29, item_name: payload.plan, item_id: payload.plan }],
      },
    });
    dispatchFrisbiiSession({ purchaseCompleted: true });

    return response.data;
  }, []);

  const onCancelEvent = useCallback(async (payload: CancelSessionPayload) => {
    await http.post('/api/payments/v2/cancel-frisbii-session', payload);
    dispatchFrisbiiSession(undefined);
  }, []);

  const onErrorFrisbiiEvent = useCallback(async (errorData: Record<string, unknown> = {}) => {
    console.error('Frisbii payment error event:', errorData);
    dispatchFrisbiiSession(undefined);
  }, []);

  return useMemo(
    () => ({
      createFrisbiiSession,
      onAcceptEvent,
      onCancelEvent,
      onErrorFrisbiiEvent,
      frisbiiSession,
    }),
    [createFrisbiiSession, onAcceptEvent, onCancelEvent, onErrorFrisbiiEvent, frisbiiSession, appStateDispatcher],
  );
};
